import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFolderOpen,
  faChevronRight,
  faChevronDown,
  faTimes,
  faArrowRight,
  faHome,
} from '@fortawesome/free-solid-svg-icons';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface FolderNode {
  id: number;
  name: string;
  parent_id: number | null;
}

interface MoveToModalProps {
  /** Human-readable name of the item being moved (for the title) */
  itemName: string;
  /** Type of item being moved — used to prevent moving a folder into itself */
  itemType: 'file' | 'folder';
  /** ID of the item being moved (used to block self-move for folders) */
  itemId: number;
  /** All folders available as destinations */
  folders: FolderNode[];
  /** Called when the user confirms the move. folderId = null means root. */
  onMove: (folderId: number | null) => void;
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper — build a set of all descendant IDs for a given folder
// (prevents circular moves: can't move a folder into itself or its own sub-tree)
// ─────────────────────────────────────────────────────────────────────────────
function getDescendantIds(folderId: number, allFolders: FolderNode[]): Set<number> {
  const result = new Set<number>();
  const queue = [folderId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const children = allFolders.filter((f) => f.parent_id === current);
    for (const child of children) {
      if (!result.has(child.id)) {
        result.add(child.id);
        queue.push(child.id);
      }
    }
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// FolderTreeNode — recursive node with expand/collapse
// ─────────────────────────────────────────────────────────────────────────────
interface FolderTreeNodeProps {
  folder: FolderNode;
  allFolders: FolderNode[];
  selectedId: number | null;
  disabledIds: Set<number>;
  onSelect: (id: number) => void;
  depth: number;
}

const FolderTreeNode: React.FC<FolderTreeNodeProps> = ({
  folder,
  allFolders,
  selectedId,
  disabledIds,
  onSelect,
  depth,
}) => {
  const children = allFolders.filter((f) => f.parent_id === folder.id);
  const hasChildren = children.length > 0;
  const [isOpen, setIsOpen] = useState(false);
  const isSelected = selectedId === folder.id;
  const isDisabled = disabledIds.has(folder.id);

  return (
    <div data-ui="move-to-modal-region-001">
      <div data-ui="move-to-modal-region-002"
        className={[
          'flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-all select-none',
          isDisabled ? 'opacity-30 cursor-not-allowed' : '',
          isSelected && !isDisabled
            ? 'bg-blue-500/30 border border-blue-500/60 text-blue-200'
            : !isDisabled
            ? 'hover:bg-white/5 border border-transparent text-white/80 hover:text-white'
            : 'text-white/30 border border-transparent',
        ].join(' ')}
        style={{ paddingLeft: `${12 + depth * 20}px` }}
        onClick={() => {
          if (isDisabled) return;
          onSelect(folder.id);
        }}
      >
        {/* Expand/collapse chevron */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled) setIsOpen((v) => !v);
            }}
            className="w-4 h-4 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors shrink-0"
          >
            <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="text-[10px]" />
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}

        <FontAwesomeIcon
          icon={isOpen ? faFolderOpen : faFolder}
          className={`shrink-0 ${isSelected && !isDisabled ? 'text-blue-400' : 'text-blue-300/70'}`}
        />
        <span className="text-sm truncate">{folder.name}</span>
      </div>

      {/* Children */}
      {isOpen && hasChildren && (
        <div data-ui="move-to-modal-region-003">
          {children.map((child) => (
            <FolderTreeNode
              key={child.id}
              folder={child}
              allFolders={allFolders}
              selectedId={selectedId}
              disabledIds={disabledIds}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MoveToModal — main component
// ─────────────────────────────────────────────────────────────────────────────

/** Sentinel value meaning "move to root (no folder)" */
const ROOT_SENTINEL = -1;

const MoveToModal: React.FC<MoveToModalProps> = ({
  itemName,
  itemType,
  itemId,
  folders,
  onMove,
  onClose,
}) => {
  // -1 = root selected; null = nothing selected yet
  const [selectedDestination, setSelectedDestination] = useState<number | null>(null);

  // Build the set of IDs that should be disabled as destinations.
  // For a folder: itself + all descendants. For a file: nothing to block.
  const disabledIds: Set<number> = React.useMemo(() => {
    if (itemType === 'folder') {
      const descendants = getDescendantIds(itemId, folders);
      descendants.add(itemId); // can't move into itself
      return descendants;
    }
    return new Set<number>();
  }, [itemType, itemId, folders]);

  // Top-level folders only (tree root layer)
  const rootFolders = folders.filter((f) => f.parent_id === null || f.parent_id === undefined);

  const handleConfirm = () => {
    if (selectedDestination === null) return;
    onMove(selectedDestination === ROOT_SENTINEL ? null : selectedDestination);
  };

  const isRootSelected = selectedDestination === ROOT_SENTINEL;
  const hasSelection = selectedDestination !== null;

  return (
    <div data-ui="move-to-modal-region-004" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div data-ui="move-to-modal-region-005" className="bg-black/30 backdrop-filter backdrop-blur-xl border border-white/20 rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div data-ui="move-to-modal-region-006" className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/20 bg-black/30">
          <div data-ui="move-to-modal-region-007">
            <h3 className="text-lg font-bold text-white leading-tight">Mover a…</h3>
            <p className="text-white/50 text-xs mt-0.5 truncate max-w-[220px]" title={itemName}>
              "{itemName}"
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>
        </div>

        {/* Folder tree */}
        <div data-ui="move-to-modal-region-008" className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5 max-h-[380px] min-h-[160px]">
          {/* Root option */}
          <div data-ui="move-to-modal-region-009"
            onClick={() => setSelectedDestination(ROOT_SENTINEL)}
            className={[
              'flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all select-none',
              isRootSelected
                ? 'bg-blue-500/30 border border-blue-500/60 text-blue-200'
                : 'hover:bg-white/5 border border-transparent text-white/80 hover:text-white',
            ].join(' ')}
          >
            <FontAwesomeIcon icon={faHome} className={isRootSelected ? 'text-blue-400' : 'text-white/40'} />
            <span className="text-sm font-medium">Raíz (sin carpeta)</span>
          </div>

          {/* Separator */}
          {rootFolders.length > 0 && <div data-ui="move-to-modal-region-010" className="border-t border-white/5 my-2" />}

          {/* Folder tree */}
          {rootFolders.map((folder) => (
            <FolderTreeNode
              key={folder.id}
              folder={folder}
              allFolders={folders}
              selectedId={selectedDestination}
              disabledIds={disabledIds}
              onSelect={(id) => setSelectedDestination(id)}
              depth={0}
            />
          ))}

          {folders.length === 0 && (
            <p className="text-center text-white/30 text-sm py-8">No hay carpetas disponibles</p>
          )}
        </div>

        {/* Footer */}
        <div data-ui="move-to-modal-region-011" className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/20 bg-black/30">
          <button
            onClick={onClose}
            className="btn-taratic btn-ghost-dark px-4 py-2 border rounded-xl text-sm transition-all"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!hasSelection}
            className={[
              'flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all',
              hasSelection
                ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                : 'bg-white/5 text-white/30 cursor-not-allowed',
            ].join(' ')}
          >
            <FontAwesomeIcon icon={faArrowRight} />
            Mover aquí
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveToModal;
